/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import React from "react";
import { fadeIn, verticalAlignedFlexbox } from "../../../config/commonCSS";
import BalanceWalletIcon from "../../../img/butter_svgs/WalletBalance.svg?react";
import WalletBalanceWarning from "../../../img/butter_svgs/WalletBalanceWarning.svg?react";
import { Amount } from "../../../types/amount";
import { Text } from "../../general/Text";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { Balance } from "store/global/getBalances";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { AmountAndUsdPrice } from "./elements/AmountAndUsdPrice";
import { Hash } from "viem";
import { getTokenTicker } from "../../../utils/tokenUtils";

export interface BalanceDetailsProps {
  price: Amount;
  balance: Balance;
  amount: Amount;
  tokenAddress: Hash;
  onBalanceClick?: () => void;
  isBalanceShown: boolean;
  isAmountShown?: boolean;
  isUsdValueShown?: boolean;
  isTradeAmountLargerThanBalance?: boolean;
}

export const BalanceDetails: React.FC<BalanceDetailsProps> = ({
  amount,
  price,
  balance,
  tokenAddress,
  onBalanceClick,
  isBalanceShown,
  isAmountShown = false,
  isUsdValueShown = false,
  isTradeAmountLargerThanBalance,
}) => {
  const usdValue = price.mul(amount).value;
  const tokenList = useTokenListByChain();
  const theme = GlobalStore.useState(s => s.theme);
  const BalanceIcon = isTradeAmountLargerThanBalance ? WalletBalanceWarning : BalanceWalletIcon;
  return (
    <BalanceSection isBalanceShown={isBalanceShown}>
      {isBalanceShown && (
        <div
          css={verticalAlignedFlexbox}
          style={{ cursor: "pointer", position: "relative", zIndex: 5 }}
        >
          <BalanceIcon width={12} height={10.5} style={{ marginRight: 4, position: "relative" }} />
          <Text
            textStyle="Paragraph"
            size="Large"
            css={fadeIn}
            color={
              isTradeAmountLargerThanBalance ? theme.font.alerts.error : theme.font.tertiary.default
            }
          >
            {balance.balance.value.gt(0)
              ? balance?.balance.toDisplay(tokenAddress, tokenList).toString()
              : "-"}
          </Text>
        </div>
      )}
      {isTradeAmountLargerThanBalance && (
        <BalanaceWarningTextContainer>
          <Text textStyle="Paragraph" size="Large" css={fadeIn} color={theme.font.alerts.error}>
            Not enough {getTokenTicker(tokenAddress, tokenList)}
          </Text>
        </BalanaceWarningTextContainer>
      )}
      <AmountAndUsdPrice
        tokenAddress={tokenAddress}
        amount={amount}
        usdValue={usdValue}
        isAmountShown={isAmountShown}
        isUsdValueShown={isUsdValueShown && !isTradeAmountLargerThanBalance}
        css={fadeIn}
      />
    </BalanceSection>
  );
};

// Sub-components

const BalanaceWarningTextContainer = styled.div`
  display: flex;
  position: relative;
`;

const BalanceSection = styled.div<{ isBalanceShown: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.theme.font.tertiary.default};
  margin-top: 4px;
  padding-left: 4px;
  width: 100%;
  justify-content: ${props => (props.isBalanceShown ? "space-between" : "end")};
  min-height: 18.2px;
  @keyframes fadeInOpacity {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;
